import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { MOVEMENT_TYPES } from "constants/index";
import { searchFilter } from "helpers/filters";
// import { recursiveSearchFilter, searchFilter } from "helpers/filters";
import {
  Article,
  GroupedOutcomeMovementArticle,
  Location,
  Movement,
  MovementArticle,
  Store,
  SubLocation,
} from "types";
import AddIncomeRow from "./AddIncomeRow";
import AddMovementArticleRow from "./AddMovementArticleRow";
import { MOVEMENT_ARTICLES_GROUPED_OUTCOME_URL } from "constants/urls";
import useCrud from "hooks/useCrud";
import Alert from "components/utils/Alert";
import { useSession } from "providers/SessionProvider";
import ArticleLocationFilters, {
  ArticleLocationLinkedFiltersValue,
} from "components/ArticleLocationFilters";
import useInfiniteApiList from "hooks/useInfiniteApiList";

type Props = {
  open: boolean;
  handleOpen: () => void;
  movement: Movement;
  articles?: Article[];
  rowIndex: number;
  handleAddMovementArticle: (newMovementArticle: MovementArticle) => void;
  operationType: string;
  locations: Location[];
  stores: Store[];
  sublocations: SubLocation[];
};

const defaultArticleLocationFilters: ArticleLocationLinkedFiltersValue = {
  locationId: undefined,
  articleCode: "",
  lote: "",
  oc: "",
};

export default function AddMovementArticleModal({
  open = true,
  handleOpen,
  movement,
  articles,
  rowIndex,
  handleAddMovementArticle,
  operationType,
  locations,
  stores,
  sublocations,
}: Props) {
  const [search, setSearch] = useState<string>("");
  const [filters, setFilters] = useState<ArticleLocationLinkedFiltersValue>(
    defaultArticleLocationFilters,
  );
  // const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { clients } = useSession();
  const currentClient = useMemo(() => {
    return clients.find((c) => c.id === movement.client_id);
  }, [clients, movement.client_id]);
  const showLote = useMemo(
    () => currentClient?.show_lote ?? false,
    [currentClient],
  );
  const showOC = useMemo(
    () => currentClient?.show_oc ?? false,
    [currentClient],
  );
  const showFechaVencimiento = useMemo(
    () => currentClient?.show_fecha_vencimiento ?? false,
    [currentClient],
  );

  const {
    fetchData: getMovementArticles,
    // setData: setMovementArticles,
  } = useCrud<GroupedOutcomeMovementArticle>(MOVEMENT_ARTICLES_GROUPED_OUTCOME_URL, {
    disableAutoFetch: true,
  });
  const getMovementArticlesRef = useRef(getMovementArticles);
  // const latestSearch = useRef(search);
  // const debouncedSearchRef = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    getMovementArticlesRef.current = getMovementArticles;
  }, [getMovementArticles]);

  const stockFilters = useMemo(
    () => ({
      clientId: movement.client_id,
      storeId: movement.store_id,
      ...filters,
    }),
    [filters, movement.client_id, movement.store_id],
  );

  const fetchMovementArticlesPage = useCallback(
    ({
      page,
      limit,
      filters: nextFilters,
    }: {
      page: number;
      limit: number;
      search: string;
      filters: typeof stockFilters;
    }) => {
      const query = new URLSearchParams({
        clientId: String(nextFilters.clientId),
        page: String(page),
        limit: String(limit),
      });

      if (nextFilters.storeId) {
        query.set("storeId", String(nextFilters.storeId));
      }

      if (nextFilters.locationId) {
        query.set("locationId", String(nextFilters.locationId));
      }

      if (nextFilters.articleCode) {
        query.set("articleCode", nextFilters.articleCode);
      }

      if (nextFilters.lote) {
        query.set("lote", nextFilters.lote);
      }

      if (nextFilters.oc) {
        query.set("oc", nextFilters.oc);
      }

      return getMovementArticlesRef.current(`?${query.toString()}`, true, {}, true);
    },
    [],
  );

  const {
    items: movement_articles,
    loading,
    loadingMore,
    hasMore,
    reset: resetMovementArticles,
    loadNextPage: loadNextMovementArticlesPage,
  } = useInfiniteApiList<GroupedOutcomeMovementArticle, typeof stockFilters>(
    {
      enabled:
        open &&
        operationType !== MOVEMENT_TYPES.INCOME.label &&
        Boolean(movement.client_id),
      limit: 10,
      filters: stockFilters,
      getItemKey: (item) => item.group_key,
      fetchPage: fetchMovementArticlesPage,
    },
  );

  /*
  Código anterior:
  const fetchMovementArticles = useCallback(
    async (searchValue = "") => {
      if (operationType !== MOVEMENT_TYPES.INCOME.label && movement.client_id) {
        const query = queryBuilder({
          clientId: movement.client_id,
          storeId: movement.store_id,
          search: searchValue,
        } as IArticleLocationQuery);
        await getMovementArticlesRef.current(query, true);
      }
      setLoading(false);
    },
    [movement.client_id, movement.store_id, operationType],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        latestSearch.current = query;
        await fetchMovementArticles(query);
      }, 1000),
    [fetchMovementArticles],
  );

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);
  */

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    setSearch(value);
    setPage(0);

    if (operationType === MOVEMENT_TYPES.INCOME.label) {
      return;
    }

    /*
    debouncedSearch.cancel();

    if (value.length >= 3) {
      latestSearch.current = value;
      setLoading(true);
      void debouncedSearch(value);
      return;
    }

    if (latestSearch.current !== "") {
      latestSearch.current = "";
      setLoading(true);
      void fetchMovementArticles();
      return;
    }
    */

    // setLoading(false);
  };

  const handleFiltersChange = (nextFilters: ArticleLocationLinkedFiltersValue) => {
    setFilters(nextFilters);
    // setPage(0);
    // setLoading(true);
    // void fetchMovementArticles(nextFilters);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const matchingArticles = useMemo(() => {
    return operationType === MOVEMENT_TYPES.INCOME.label
      ? search.length >= 3
        ? searchFilter(search, articles || [], ["code", "description"])
        : (articles ?? [])
      : [];
  }, [search, articles, operationType]);

  const paginatedArticles = useMemo(() => {
    return matchingArticles.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [matchingArticles, page, rowsPerPage]);

  const filteredMovementArticles = useMemo(
    () => {
      return movement_articles ?? [];
    },
    [movement_articles],
  );

  /*
  const filteredMovementArticles = useMemo(
    () => {
      if (search.length < 3) {
        return movement_articles ?? [];
      }

      return (
      recursiveSearchFilter(
        search,
        movement_articles ?? [],
        ["article_locations"],
        [
          ["article", "code"],
          ["article", "description"],
        ],
      ) || []
      );
    },
    [movement_articles, search],
  );
  */

  // const paginatedMovementArticles = useMemo(() => {
  //   return filteredMovementArticles.slice(
  //     page * rowsPerPage,
  //     page * rowsPerPage + rowsPerPage,
  //   );
  // }, [filteredMovementArticles, page, rowsPerPage]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearch("");
      setFilters(defaultArticleLocationFilters);
      // setLoading(false);

      if (
        operationType !== MOVEMENT_TYPES.INCOME.label &&
        movement.client_id
      ) {
        // debouncedSearchRef.current?.cancel();
        // latestSearch.current = "";
        // setLoading(true);
        // void fetchMovementArticles(defaultArticleLocationFilters);
      } else if (operationType !== MOVEMENT_TYPES.INCOME.label) {
        // setMovementArticles([]);
        resetMovementArticles();
      }
    }
  }, [
    open,
    operationType,
    movement.client_id,
    resetMovementArticles,
  ]);

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || loadingMore) {
        return;
      }

      const target = event.currentTarget;
      const reachedBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (reachedBottom) {
        loadNextMovementArticlesPage();
      }
    },
    [hasMore, loadingMore, loadNextMovementArticlesPage],
  );

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {operationType === MOVEMENT_TYPES.INCOME.label ? (
              <TextField
                id="product-search"
                label="Buscar artículo"
                fullWidth
                value={search}
                onChange={handleChange}
              />
            ) : (
              /*
              <TextField
                id="product-search"
                label="Buscar artículo"
                fullWidth
                value={search}
                onChange={handleChange}
              />
              */
              <ArticleLocationFilters
                clientId={movement.client_id}
                storeId={movement.store_id}
                value={filters}
                onChange={handleFiltersChange}
                disabled={loading || !movement.client_id}
              />
            )}
          </Grid>
          {loading ? (
            <Grid item xs={12} display={"flex"} justifyContent={"center"}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: "20rem" }}
                  onScroll={
                    operationType === MOVEMENT_TYPES.INCOME.label
                      ? undefined
                      : handleTableScroll
                  }
                >
                  {/* Si se muestran los artículos de income */}
                  {operationType === MOVEMENT_TYPES.INCOME.label && articles ? (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Codigo
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Descripción
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Contenedor
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Unidades por pallet
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            UM
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 140 }}>
                            Presentación
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Cantidad
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Ubicación
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Sub-ubicación
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Cantidad por Contenedor
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Estado
                          </TableCell>
                          {showLote && (
                            <TableCell align="center" style={{ minWidth: 170 }}>
                              Lote
                            </TableCell>
                          )}
                          {showOC && (
                            <TableCell align="center" style={{ minWidth: 170 }}>
                              Orden de Compra
                            </TableCell>
                          )}
                          {showFechaVencimiento && (
                            <TableCell align="center" style={{ minWidth: 170 }}>
                              Fecha de Vencimiento
                            </TableCell>
                          )}
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Observación
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ minWidth: 170 }}
                          ></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedArticles.map((article: Article) => {
                          return (
                            <AddIncomeRow
                              key={article.id}
                              movement={movement}
                              articleInput={article}
                              rowIndex={rowIndex}
                              onMovementArticleAdd={handleAddMovementArticle}
                              locations={locations}
                              stores={stores}
                              showLote={showLote}
                              showOC={showOC}
                              showFechaVencimiento={showFechaVencimiento}
                              sublocations={sublocations}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : filteredMovementArticles.length > 0 ? (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Codigo
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Descripción
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Unidades por pallet
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Cantidad
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Cantidad por Contenedor
                          </TableCell>
                          {showLote && (
                            <TableCell align="center" style={{ minWidth: 170 }}>
                              Lote
                            </TableCell>
                          )}
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Fecha Ingreso
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Ubicación
                          </TableCell>
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Sub-ubicación
                          </TableCell>
                          {showOC && (
                            <TableCell align="center" style={{ minWidth: 170 }}>
                              Orden de Compra
                            </TableCell>
                          )}
                          {showFechaVencimiento && (
                            <TableCell align="center" style={{ minWidth: 170 }}>
                              Fecha de Vencimiento
                            </TableCell>
                          )}
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Observación
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ minWidth: 170 }}
                          ></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredMovementArticles.map((groupedMovementArticle) => (
                          <AddMovementArticleRow
                            key={groupedMovementArticle.group_key}
                            movement={movement}
                            groupedMovementArticle={groupedMovementArticle}
                            rowIndex={rowIndex}
                            onMovementArticleAdd={handleAddMovementArticle}
                            showLote={showLote}
                            showOC={showOC}
                            showFechaVencimiento={showFechaVencimiento}
                            locations={locations}
                          />
                        ))}
                        {loadingMore && (
                          <TableRow>
                            <TableCell colSpan={13} align="center">
                              Cargando...
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <Alert
                      severity="info"
                      text="No hay artículos con stock disponible para este cliente."
                    />
                    /*
                    search.length >= 3 ? (
                      <Alert
                        severity="warning"
                        text="No se encontraron resultados para esta búsqueda"
                      />
                    ) : (
                      <Alert
                        severity="info"
                        text="Ingresa el código o la descripción del producto"
                      />
                    )
                    */
                  )}
                </TableContainer>
                {operationType === MOVEMENT_TYPES.INCOME.label && articles ? (
                  <TablePagination
                    component="div"
                    count={matchingArticles.length || 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                ) : filteredMovementArticles.length > 0 ? (
                  /*
                  <TablePagination
                    component="div"
                    count={filteredMovementArticles.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                  */
                  null
                ) : null}
              </Grid>
              <Grid
                item
                xs={12}
                display={"flex"}
                justifyContent={"end"}
                gap={2}
              >
                <Button
                  variant="outlined"
                  onClick={() => handleOpen()}
                  data-cy={"finish-adding-button"}
                >
                  Cerrar
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </Modal>
  );
}

